import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, BarChart2, BookOpen, Copy, Heart, KeyRound, Layers, ListChecks, ShieldCheck, Unlock, Users, Zap,
} from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { app } from '../lib/firebase.js';
import SocHeader from '../components/SocHeader.jsx';
import Footer from '../components/Footer.jsx';
import Toast from '../components/Toast.jsx';
import { useToast } from '../hooks/useToast.js';
import { useAuth } from '../context/AuthContext.jsx';
import { liveHubs, pipelineHubs } from '../data/hubs.js';

export default function Home() {
  const { user } = useAuth();
  const { toasts, toast, dismiss } = useToast();

  const [cName,    setCName]    = useState('');
  const [cEmail,   setCEmail]   = useState('');
  const [cSubject, setCSubject] = useState('');
  const [cMessage, setCMessage] = useState('');
  const [cStatus,  setCStatus]  = useState('idle'); // idle | sending | success | error
  const [cErr,     setCErr]     = useState('');

  const [nEmail,   setNEmail]   = useState('');
  const [nSending, setNSending] = useState(false);

  const onSubscribe = async () => {
    const email = nEmail.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    setNSending(true);
    try {
      const db = getFirestore(app);
      await setDoc(doc(db, 'subscribers', email), { email, subscribedAt: serverTimestamp() });
      httpsCallable(getFunctions(app), 'sendSubscribeNotification')({ email }).catch(
        err => console.error('[subscribe notify error]', err)
      );
      setNEmail('');
      toast('Subscribed. You\'ll be first to know when new hubs drop.', 'success');
    } catch (err) {
      console.error('[subscribe error]', err);
      toast('Subscription failed — please try again.', 'error');
    } finally {
      setNSending(false);
    }
  };

  const onContactSubmit = async (e) => {
    e.preventDefault();
    setCStatus('sending'); setCErr('');
    try {
      const fn = httpsCallable(getFunctions(app), 'sendContactEmail');
      await fn({ name: cName, email: cEmail, subject: cSubject, message: cMessage, honeypot: '' });
      setCStatus('success');
      setCName(''); setCEmail(''); setCSubject(''); setCMessage('');
      toast('Transmission sent. We\'ll reply to your inbox shortly.', 'success');
    } catch (err) {
      console.error('[contact form error]', err);
      setCStatus('error');
      setCErr(err.message || 'Transmission failed. Please try again.');
      toast(err.message || 'Transmission failed. Please try again.', 'error');
    }
  };

  const hubMeta = (h) => (
    <div className="hub-meta-row">
      <span><strong>{h.domains}</strong>&nbsp;Domains</span>
      <span><strong>{h.qs}</strong>&nbsp;Practice Qs</span>
      <span><strong>{h.cards}</strong>&nbsp;Cards</span>
      <span>{h.plan ? <><strong>{h.plan}</strong>&nbsp;Wk Plan</> : <>—&nbsp;Wk Plan</>}</span>
    </div>
  );

  return (
    <>
      <SocHeader />
      <Toast toasts={toasts} dismiss={dismiss} />

      <main id="main">
        {/* ===== HERO ===== */}
        <section className="hero">
          <div className="hero-content">
            <div className="hero-eyebrow">Mission Brief &middot; Military &amp; Veteran Transition</div>
            <h1 className="hero-title">You served.<span className="accent"> Now own the tech.</span></h1>
            <p className="hero-lede">
              VetTech Learning Hub is built for military service members and veterans making the transition into technical fields.
              Every study hub is a fully-loaded tactical environment — domain breakdowns, flashcards, timed practice exams, and structured study plans.
              The discipline you built in uniform is exactly what this industry rewards. We give you the roadmap.
            </p>
            <div className="hero-cta">
              <a href="#live" className="btn">Enter a Live Hub <ArrowRight aria-hidden="true" /></a>
              {!user && <Link to="/signup" className="btn btn-secondary-dark">Create Free Account</Link>}
              <a href="#mission" className="text-link">Our Mission <ArrowRight aria-hidden="true" /></a>
            </div>
          </div>
          <div className="vertical-phrase" aria-hidden="true">
            <span>Training</span><span>Veterans</span><span>Building</span><span>Careers</span>
          </div>
          <div className="trust-bar" aria-label="Platform at a glance">
            <div className="trust-item"><BookOpen aria-hidden="true" /><div><div className="stat-value"><span className="accent">04</span></div><div className="stat-label">Hubs Live</div></div></div>
            <div className="trust-item"><Layers aria-hidden="true" /><div><div className="stat-value">11</div><div className="stat-label">In Pipeline</div></div></div>
            <div className="trust-item"><ListChecks aria-hidden="true" /><div><div className="stat-value"><span className="accent">270+</span></div><div className="stat-label">Practice Qs</div></div></div>
            <div className="trust-item"><Copy aria-hidden="true" /><div><div className="stat-value">15+</div><div className="stat-label">Flashcard Decks</div></div></div>
          </div>
        </section>

        {/* ===== MISSION ===== */}
        <section id="mission">
          <div className="section-head">
            <span className="section-tag">Who We Serve</span>
            <h2>Mission Brief</h2>
          </div>
          <div className="mission-block">
            <div className="mission-left">
              <div className="mission-eyebrow">Founded by a Marine. Built for all who served.</div>
              <h3 className="mission-headline">You already know how to execute.<br /><span className="accent">We teach you the tech.</span></h3>
              <p>The gap between military service and the technical workforce is real — but it's not as wide as it looks. The discipline, attention to detail, ability to operate under pressure, and mission-first mindset that the military builds are exactly the attributes the tech industry needs and rarely finds.</p>
              <p>VetTech Learning Hub exists to close that gap. Whether you're separating next month, two years out and looking to level up, or active-duty building skills on the side — we give you structured, no-fluff study environments designed to get you certified, employed, and advancing.</p>
              <p>Every hub is <strong>free to use</strong>. Every resource is vetted. Every piece of content is written like a briefing, not a textbook — because that's how we were trained to absorb information, and it works.</p>
              <div className="hero-cta" style={{ marginTop: '1.75rem' }}>
                <a href="#live" className="btn">Start Studying <ArrowRight aria-hidden="true" /></a>
                <a href="#support" className="btn btn-ghost">Support the Mission</a>
              </div>
            </div>
            <div className="mission-right">
              <div className="mission-pillar">
                <div className="pillar-icon">01</div>
                <div><strong>Mission-ready content</strong><span>Every hub built from official exam outlines. No filler, no fluff — only what's on the test and why it matters on the job.</span></div>
              </div>
              <div className="mission-pillar">
                <div className="pillar-icon">02</div>
                <div><strong>Veteran-built</strong><span>Designed by a USMC veteran who made the transition. The content speaks your language because it was written in it.</span></div>
              </div>
              <div className="mission-pillar">
                <div className="pillar-icon">03</div>
                <div><strong>Free to use</strong><span>No paywalls, no subscriptions, no accounts required. Every study hub is open — because the mission is access, not revenue.</span></div>
              </div>
              <div className="mission-pillar">
                <div className="pillar-icon">04</div>
                <div><strong>Always expanding</strong><span>New hubs are built continuously. AWS, Azure, CompTIA, Google Cloud — the full certification roadmap for a technical career is the target.</span></div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== LIVE HUBS ===== */}
        <section id="live" className="band band-alt">
          <div className="section-head">
            <span className="section-tag">Status &middot; 04 Online</span>
            <h2>Live Hubs</h2>
          </div>
          <div className="hub-grid">
            {liveHubs.map((h) => (
              <article key={h.id} className="hub-card hub-live">
                <div className="hub-card-header">
                  <div className="hub-id">Hub&nbsp;{h.num}</div>
                  <span className="hub-badge hub-badge-live">Live</span>
                </div>
                <div className="hub-vendor">{h.vendor}</div>
                <h3 className="hub-title">{h.title}</h3>
                <div className="hub-code">{h.code}</div>
                <p className="hub-desc">{h.desc}</p>
                {hubMeta(h)}
                <div className="hub-actions"><Link to={`/hub/${h.id}`} className="btn">Enter Hub <ArrowRight aria-hidden="true" /></Link></div>
              </article>
            ))}
          </div>
        </section>

        {/* ===== PIPELINE ===== */}
        <section id="pipeline">
          <div className="section-head">
            <span className="section-tag">Inbound &middot; 11 Queued</span>
            <h2>Pipeline</h2>
          </div>
          <div className="hub-grid hub-grid-pipeline">
            {pipelineHubs.map((h) => (
              <article key={h.code} className="hub-card hub-soon">
                <div className="hub-card-header">
                  <div className="hub-id">Hub&nbsp;{h.num}</div>
                  <span className="hub-badge hub-badge-soon">Queued</span>
                </div>
                <div className="hub-vendor">{h.vendor}</div>
                <h3 className="hub-title">{h.title}</h3>
                <div className="hub-code">{h.code}</div>
                <p className="hub-desc">{h.desc}</p>
                {hubMeta(h)}
                <div className="hub-actions"><span className="hub-eta">ETA: Coming Soon</span></div>
              </article>
            ))}
          </div>
        </section>

        {/* ===== LOADOUT ===== */}
        <section id="loadout" className="band band-alt">
          <div className="section-head">
            <span className="section-tag">Standard Loadout</span>
            <h2>What's Inside Every Hub</h2>
          </div>
          <ul className="feature-list">
            <li><strong>Domain breakdown</strong>Every exam domain decomposed into objectives with weights, key terms, and exam-day focus areas. Built from the official outline — no guesswork.</li>
            <li><strong>Interactive flashcards</strong>30–40 cards per domain. Flip, tag as Known or Review, shuffle, track progress. localStorage persistence — your session survives a refresh.</li>
            <li><strong>Full practice exam</strong>90-question timed exam with question navigator, flag system, auto-save resume, and full results review with per-question explanations and domain breakdown.</li>
            <li><strong>Objective drills</strong>Per-domain 30-question timed drills. Isolate weak domains and grind them separately. Same navigator, flag, and review system as the full exam.</li>
            <li><strong>Study plan</strong>12-week cadence with checkable weekly milestones, daily hour targets, and a final-week sprint protocol. Copy-to-clipboard export.</li>
            <li><strong>Cheat sheet</strong>Printable single-page reference built for last-week review. Key terms, formulas, acronyms, and quick-reference grids. Print as PDF from the browser.</li>
            <li><strong>Curated resources</strong>Vetted books, video courses, free labs, and practice exam vendors. No invented URLs, no filler — only real, confirmed sources.</li>
          </ul>
        </section>

        {/* ===== AI TUTOR ===== */}
        <section id="ai-tutor">
          <div className="section-head">
            <span className="section-tag">Member Feature &middot; BYOK</span>
            <h2>AI Exam Tutor</h2>
          </div>
          <div className="mission-block">
            <div className="mission-left">
              <div className="mission-eyebrow">Your key. Your AI. Zero data collected.</div>
              <h3 className="mission-headline">Ask anything — get instant,<br /><span className="accent">exam-accurate answers.</span></h3>
              <p>The AI panel lives on every page once you're signed in. Ask for concept explanations, acronym breakdowns, exam-day strategy, or a quick quiz on any domain — right inside your study session. No tab switching. No context loss.</p>
              <p>VetTech uses a <strong>Bring Your Own Key (BYOK)</strong> model. You connect your own API key from OpenAI, Anthropic, Google Gemini, or a local Ollama instance. Your key is stored <strong>only in your browser</strong> — it never touches VetTech servers, never gets logged, never gets shared. Traffic flows directly from your browser to your chosen AI provider.</p>
              <p>This is how AI should work: the intelligence is yours, the cost is transparent, and the privacy is absolute.</p>
              <div className="hero-cta" style={{ marginTop: '1.75rem' }}>
                {user ? (
                  <Link to="/ai-settings" className="btn btn-primary"><Zap aria-hidden="true" /> Configure AI <ArrowRight aria-hidden="true" /></Link>
                ) : (
                  <>
                    <Link to="/signup" className="btn btn-signup">Create Account to Unlock <ArrowRight aria-hidden="true" /></Link>
                    <Link to="/signin" className="btn btn-ghost">Sign In</Link>
                  </>
                )}
              </div>
            </div>
            <div className="mission-right">
              <div className="mission-pillar">
                <div className="pillar-icon"><Zap aria-hidden="true" /></div>
                <div><strong>Instant answers</strong><span>Ask about any concept, acronym, or exam objective. Get a concise, accurate response tuned for cert prep — no generic AI fluff, no hallucinated content.</span></div>
              </div>
              <div className="mission-pillar">
                <div className="pillar-icon"><KeyRound aria-hidden="true" /></div>
                <div><strong>Bring your own key</strong><span>Connect OpenAI GPT-4o, Anthropic Claude, Google Gemini, or a local Ollama model. Switch providers at any time. You choose the model. You control the cost.</span></div>
              </div>
              <div className="mission-pillar">
                <div className="pillar-icon"><ShieldCheck aria-hidden="true" /></div>
                <div><strong>Zero server exposure</strong><span>Your API key lives in your browser's localStorage only. VetTech never sees it, never stores it, and never proxies your requests. It never leaves your device.</span></div>
              </div>
              <div className="mission-pillar">
                <div className="pillar-icon"><BarChart2 aria-hidden="true" /></div>
                <div><strong>Transparent usage</strong><span>Live token counter and call tracker built in. Know exactly what you're spending. No hidden VetTech fee — you pay only your AI provider's standard rate.</span></div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== TESTIMONIALS ===== */}
        <section id="testimonials" className="band band-alt">
          <div className="section-head">
            <span className="section-tag">Operator Feedback</span>
            <h2>From the Field</h2>
          </div>
          <div className="grid grid-3">
            <div className="testimonial-card">
              <div className="testimonial-quote">I separated after 8 years in the Army with zero IT background. The Security+ hub gave me structure I recognized — it felt like a training schedule, not a study guide. Passed first attempt.</div>
              <div className="testimonial-meta">
                <div className="testimonial-name">Staff Sgt. Marcus T.</div>
                <div className="testimonial-cert"><span className="hub-badge hub-badge-live">Security+ SY0-701</span></div>
                <div className="testimonial-branch">U.S. Army &middot; 8 Years</div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-quote">Coming from an intel background, the AI/ML concepts clicked fast once I had the domain breakdown in front of me. The AWS hub is structured exactly how I think. Cleared it in 6 weeks.</div>
              <div className="testimonial-meta">
                <div className="testimonial-name">PO2 Rina V.</div>
                <div className="testimonial-cert"><span className="hub-badge hub-badge-live">AWS AIF-C01</span></div>
                <div className="testimonial-branch">U.S. Navy &middot; 6 Years</div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-quote">Free, no login, works offline. This is the most operator-friendly study tool I've found. The practice exams are brutal in the best way. My mock score jumped 18 points between runs.</div>
              <div className="testimonial-meta">
                <div className="testimonial-name">Cpl. Darnell W.</div>
                <div className="testimonial-cert"><span className="hub-badge hub-badge-live">Security+ SY0-701</span></div>
                <div className="testimonial-branch">U.S. Marine Corps &middot; 4 Years</div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== SUPPORT / DONATE ===== */}
        <section id="support">
          <div className="section-head">
            <span className="section-tag">Keep It Free</span>
            <h2>Support the Mission</h2>
          </div>
          <div className="donate-block">
            <div className="donate-content">
              <div className="hero-eyebrow">Every hub is free. Every resource is open.</div>
              <h3 className="donate-headline">Veterans helped us get here.<br /><span className="accent">Help us reach the next one.</span></h3>
              <p>VetTech Learning Hub runs on zero subscription revenue. No ads, no paywalls, no data collection. If these resources helped you land a cert, a job, or a raise — consider supporting the mission so we can build more hubs for the next service member coming through the door.</p>
              <p>100% of donations go toward building new study hubs, maintaining existing content, and keeping the platform free for every veteran, active-duty member, and military spouse who needs it.</p>
              <div className="donate-actions">
                <div><button className="btn btn-donate" type="button" onClick={() => alert('Donation processing coming soon — thank you for your support.')}><Heart aria-hidden="true" /> Donate &amp; Support VetTech</button></div>
                <p className="donate-note">Donation processing coming soon — thank you for your support.</p>
              </div>
            </div>
            <div className="donate-stats">
              <div className="donate-stat"><div className="donate-stat-icon"><Layers aria-hidden="true" /></div><div><span className="donate-stat-num accent">15</span><span className="donate-stat-label">Hubs Planned</span></div></div>
              <div className="donate-stat"><div className="donate-stat-icon"><Unlock aria-hidden="true" /></div><div><span className="donate-stat-num">$0</span><span className="donate-stat-label">Cost to Access</span></div></div>
              <div className="donate-stat"><div className="donate-stat-icon"><Users aria-hidden="true" /></div><div><span className="donate-stat-num accent">∞</span><span className="donate-stat-label">Veterans Served</span></div></div>
            </div>
          </div>
        </section>

        {/* ===== NEWSLETTER ===== */}
        <section id="newsletter" className="band band-alt">
          <div className="section-head">
            <span className="section-tag">Intel Updates</span>
            <h2>Stay in the Loop</h2>
          </div>
          <div className="form-block">
            <div className="form-block-left">
              <p className="subhead">New hub dropping? You'll know first.</p>
              <p>Drop your email and we'll notify you when new study hubs go live, when content gets updated, and when new resources are added. No spam. No marketing. Just signal.</p>
            </div>
            <div className="form-block-right form-card">
              <div className="form-group">
                <label className="form-label" htmlFor="newsletter-email">Email address</label>
                <div className="newsletter-row">
                  <input
                    type="email"
                    id="newsletter-email"
                    className="form-input"
                    placeholder="you@example.com"
                    autoComplete="email"
                    value={nEmail}
                    onChange={e => setNEmail(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && onSubscribe()}
                    disabled={nSending}
                  />
                  <button
                    type="button"
                    className="btn"
                    onClick={onSubscribe}
                    disabled={nSending || !nEmail.trim()}
                  >
                    {nSending ? 'Subscribing…' : 'Subscribe'}
                  </button>
                </div>
              </div>
              <p className="form-hint">No spam. Unsubscribe any time.</p>
            </div>
          </div>
        </section>

        {/* ===== CONTACT ===== */}
        <section id="contact">
          <div className="section-head">
            <span className="section-tag">Open Comms</span>
            <h2>Contact</h2>
          </div>
          <div className="form-block">
            <div className="form-block-left">
              <p className="subhead">Questions, feedback, or want to connect?</p>
              <p>Whether you're a veteran looking for guidance on where to start, an organization interested in partnering, or someone who wants to contribute — we want to hear from you.</p>
              <div className="callout">
                <strong>Direct Line</strong>
                <a href="mailto:info@vettechlearning.com">info@vettechlearning.com</a>
              </div>
            </div>
            <div className="form-block-right form-card">
              {cStatus === 'success' ? (
                <div>
                  <div className="form-msg success">Transmission sent — we'll reply to your inbox shortly. Standby.</div>
                  <button type="button" className="btn btn-ghost" onClick={() => setCStatus('idle')}>Send Another <ArrowRight aria-hidden="true" /></button>
                </div>
              ) : (
                <form className="contact-form" onSubmit={onContactSubmit} noValidate>
                  {cStatus === 'error' && <div className="form-msg error" role="alert">Transmission failed — {cErr}</div>}
                  <div className="form-row-2">
                    <div className="form-group">
                      <label className="form-label" htmlFor="contact-name">Name</label>
                      <input type="text" id="contact-name" className="form-input" placeholder="Your name" autoComplete="name" required value={cName} onChange={e => setCName(e.target.value)} disabled={cStatus === 'sending'} />
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="contact-email">Email</label>
                      <input type="email" id="contact-email" className="form-input" placeholder="you@example.com" autoComplete="email" required value={cEmail} onChange={e => setCEmail(e.target.value)} disabled={cStatus === 'sending'} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="contact-subject">Subject</label>
                    <input type="text" id="contact-subject" className="form-input" placeholder="Subject" value={cSubject} onChange={e => setCSubject(e.target.value)} disabled={cStatus === 'sending'} />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="contact-message">Message</label>
                    <textarea id="contact-message" className="form-input form-textarea" placeholder="Your message" rows="5" required value={cMessage} onChange={e => setCMessage(e.target.value)} disabled={cStatus === 'sending'}></textarea>
                  </div>
                  <button type="submit" className="btn" disabled={cStatus === 'sending' || !cName.trim() || !cEmail.trim() || !cMessage.trim()}>
                    {cStatus === 'sending' ? 'Transmitting…' : 'Send Message'} <ArrowRight aria-hidden="true" />
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}